import { group, type InternMap } from 'd3-array';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import { normalizeSuggestedTitles } from '@revelio/ui';

import type { SelectionListItem as SharedSelectionListItem } from '../../../../shared/selection-lists/types';
import type { AllSelectionListsWithSkillTaxonomyKey } from '../types/filters';

export interface TreeData {
  id: string;
  name: string;
  depth: number;
  children?: TreeData[];
  linkedIds?: string[];
  nodeCount?: number;
  info?: string;
  suggestedTitles?: string[];
  levelId?: AllSelectionListsWithSkillTaxonomyKey;
}

type SelectionListItem = SharedSelectionListItem & {
  linkedParentIds?: string[];
  [key: string]: unknown;
};
type SelectionList = SelectionListItem[];

const warnedDuplicateRootList = new Set<string>();
const warnedDuplicateChildList = new Set<string>();

type SelectionListWithId = {
  selectionListId: AllSelectionListsWithSkillTaxonomyKey;
  selectionList: SelectionList;
};

type SelectionListItemWithParentIds = SelectionListItem & {
  linkedParentIds?: string[];
};

type ExpandedItem = SelectionListItem & {
  [key: string]: string | string[] | undefined;
};

type ChildMap = InternMap<string, SelectionListItemWithParentIds[]>;

export type NestSelectionLists = SelectionListWithId[];

export const nestSelectionLists = (
  selectionLists: NestSelectionLists
): TreeData[] => {
  if (!selectionLists || selectionLists.length === 0) {
    return [];
  }

  const selectionListIds = selectionLists.map((l) => l.selectionListId);

  const rootListId = selectionLists[0]?.selectionListId;
  const rawRootList = selectionLists[0]?.selectionList ?? [];
  const uniqueItems = uniqBy(rawRootList, 'id');

  if (uniqueItems.length < rawRootList.length) {
    const key = String(rootListId ?? 'unknown');
    if (!warnedDuplicateRootList.has(key)) {
      warnedDuplicateRootList.add(key);
      console.info(
        `[nestSelectionLists] Duplicate items in ${key} selection list (deduped)`
      );
    }
  }

  return nestRecursive({
    selectionListItems: uniqueItems,
    childMaps: prepareChildMaps(selectionLists),
    depth: 0,
    selectionListIds,
  });
};

const prepareChildMaps = (selectionLists: NestSelectionLists): ChildMap[] => {
  return selectionLists
    .slice(1)
    .map((list, i) => {
      /** For case where d[selectionLists[i].id] is an array instead of a single string
       * (i.e. when a selection list item can have multiple parents)
       * duplicate items in the selectionList first */
      const parentListName = selectionLists[i]?.selectionListId as string;
      if (!parentListName) return null;

      const listToGroup =
        list.selectionList?.flatMap((item) => {
          // Handle GraphQL alias differences between parent list ids and child fields
          // Example: city items reference parent MSA via 'metro_area', not 'msa'
          const resolvedParentFieldName =
            parentListName === 'msa' ? 'metro_area' : parentListName;

          const parentIds = (item as ExpandedItem)[resolvedParentFieldName];

          const parentIdArray = (
            isArray(parentIds) ? parentIds : [parentIds]
          ) as string[];

          return parentIdArray
            .filter((parentId) => parentId !== undefined && parentId !== null)
            .map((parentId) => ({
              ...item,
              // Normalize the parent reference onto the expected parent list name
              [parentListName]: parentId,
              ...(parentIdArray.length > 1
                ? { linkedParentIds: parentIdArray }
                : {}),
            }));
        }) ?? [];

      const groupedResult = group(
        listToGroup,
        (d) => (d as ExpandedItem)[parentListName] as string
      );

      // Fix duplicates within each parent group
      groupedResult.forEach((items, parentKey) => {
        if (!Array.isArray(items)) return;

        const uniqueItems = uniqBy(items, 'id');

        if (uniqueItems.length < items.length) {
          const duplicates = Object.values(groupBy(items, 'id')).filter(
            (group) => group.length > 1
          );

          const trueDuplicates = duplicates.filter((group) =>
            group.every((item) => isEqual(item, group[0]))
          );

          if (trueDuplicates.length > 0) {
            const childKey = String(list.selectionListId);
            if (!warnedDuplicateChildList.has(childKey)) {
              warnedDuplicateChildList.add(childKey);
              console.info(
                `[nestSelectionLists] Duplicate item id ${trueDuplicates
                  .map((group) => group[0]?.id)
                  .join(', ')} in ${childKey} selection list (deduped)`
              );
            }
          }

          groupedResult.set(parentKey, uniqueItems);
        }
      });
      return groupedResult;
    })
    .filter((map) => map !== null) as ChildMap[];
};

// Each arborist tree branch is considered a new tree root, resetting the node level to 0,
// so let's define a depth property to track the actual nesting level.
type NestRecursiveProps = {
  selectionListItems: SelectionListItemWithParentIds[];
  childMaps: ChildMap[];
  nestedParentId?: string;
  depth: number;
  selectionListIds?: AllSelectionListsWithSkillTaxonomyKey[];
};

const nestRecursive = ({
  selectionListItems,
  childMaps,
  nestedParentId,
  depth,
  selectionListIds,
}: NestRecursiveProps): TreeData[] => {
  return selectionListItems
    .filter(
      (item): item is SelectionListItemWithParentIds & { id: string } =>
        !!item?.id
    )
    .map((item): TreeData => {
      const nestedId = nestedParentId
        ? `${nestedParentId}_${item.id}`
        : item.id;
      const children = childMaps[depth]?.get(item.id);
      const suggestedTitles = extractSuggestedTitles(item);
      const levelId = selectionListIds?.[depth];
      const name = getNodeName(item);
      const info = extractNodeInfo(item);

      return {
        id: nestedId,
        name,
        depth,
        ...(levelId !== undefined && { levelId }),
        nodeCount: extractNodeCount(item),
        ...(info !== undefined ? { info } : {}),
        ...(suggestedTitles && { suggestedTitles }),
        ...(item?.linkedParentIds && {
          linkedIds: item.linkedParentIds
            .filter((id: string) => id !== nestedParentId?.split('_').at(-1))
            .map(
              (id: string) =>
                `${nestedParentId?.split('_').slice(0, -1).join('_')}_${id}_${
                  item.id
                }`
            ),
        }),
        ...(children && {
          children: nestRecursive({
            selectionListItems: children,
            childMaps,
            nestedParentId: nestedId,
            depth: depth + 1,
            selectionListIds,
          }),
        }),
      };
    });
};

const extractNodeCount = (item: SelectionListItem): number | undefined => {
  if (
    item.__typename === 'RicsRefInfo' ||
    item.__typename === 'CompanyRefInfo'
  ) {
    return item.headcount !== null ? (item.headcount as number) : undefined;
  }

  return undefined;
};

const extractSuggestedTitles = (
  item: SelectionListItem
): string[] | undefined => {
  if (
    item.__typename === 'RoleTaxonomyRefInfo' &&
    item.rawTitles !== undefined &&
    item.rawTitles !== null
  ) {
    return normalizeSuggestedTitles(item.rawTitles);
  }

  if (item.__typename === 'SkillRefInfo' && item?.topSkills) {
    return normalizeSuggestedTitles(item.topSkills);
  }

  return undefined;
};

const extractNodeInfo = (item: SelectionListItem): string | undefined => {
  if (
    item.__typename === 'RicsRefInfo' ||
    item.__typename === 'CompanyRefInfo'
  ) {
    return undefined;
  }

  if (typeof item.info === 'string' && item.info.trim() !== '') {
    return item.info;
  }

  return undefined;
};

const getNodeName = (item: SelectionListItem): string =>
  item.shortName || item.longName || String(item.id);
